import { inject } from '@angular/core';
import { Router, UrlTree, type CanActivateFn, type CanMatchFn } from '@angular/router';
import { deriveFlowMode, isUsableAlbum, type FC_PhotoBookDTO, type ProjectFlowMode } from '@lite-workspace/dto-fotoclass-book';
import { PROJECT_URL, ProjectRoleService } from '../../../core/project-role';
import { ProjectsFacade } from '../domain/application/projects.facade';
import { ACCESS_READ_LIMIT_MS, VerdictBudget } from '../../../core/route-wait';
import { withTimeout } from '../domain/utils/with-timeout.util';

// A CanMatchFn sees only its OWN route's segments, and `:projectId` belongs to the parent — so the
// id comes off the navigating URL (the same regex the role service matches project surfaces with).
function navigatingProjectId(router: Router): string | null {
  const url = router.getCurrentNavigation()?.extractedUrl.toString() ?? router.url;
  return url.match(PROJECT_URL)?.[1] ?? null;
}

/**
 * The organizer (and an owner previewing as one) gets the students screen on the project root;
 * everyone else falls through to the next `''` route — today's redirect to «Альбомы».
 *
 * Роль читается из кэша вкладки, а на промахе — с сервера (`loadRoles`): прогревающий кэш эффект бежит
 * только после NavigationEnd, то есть ПОСЛЕ гварда, и на свежей вкладке с набранным адресом синхронное
 * чтение отвечало бы «не организатор» кому угодно. Кэш, а не `loadRolesFresh`, потому что этот
 * canMatch срабатывает на каждый братский переход внутри проекта; смену личности в слоте bff-куки
 * ловит родительский `clientCabinetGuard` — он ходит на сервер сам.
 */
export const organizerHomeGuard: CanMatchFn = async () => {
  const roles = inject(ProjectRoleService);
  const budget = inject(VerdictBudget);
  const pid = navigatingProjectId(inject(Router));
  if (!pid) return false;
  try {
    // Роль не успела за бюджет — то же, что неизвестна: поздний увод на «Учеников» делает гвард раздела.
    if (!(await budget.within(roles.loadRoles(pid)))) return false;
  } catch {
    // Роль неизвестна — маршрут падает в обычный кабинет. Отдать узкий экран организатора значило бы
    // запереть на нём фотографа: соседний гвард на отказе тоже уводил бы его с каждого раздела сюда
    // обратно, и до починки `/my-roles` собственный проект остался бы без альбомов и настроек.
    return false;
  }

  return roles.isProjectOrganizerOnly(pid);
};

/** No other project section is the organizer's: a typed address returns them to the students screen. */
export const organizerSectionRedirectGuard: CanActivateFn = async (route, state) => {
  const pid = route.parent?.paramMap.get('projectId') ?? route.paramMap.get('projectId');
  const roles = inject(ProjectRoleService);
  const router = inject(Router);
  const budget = inject(VerdictBudget);
  if (!pid) return true;
  // Роль неизвестна — раздел отдаём: увод отсюда держится на подтверждённом «он только организатор»,
  // иначе сбой `/my-roles` отнимал бы разделы у фотографа. Данные всё равно закрывает сервер.
  const verdict = roles
    .loadRoles(pid)
    .then(() => (roles.isProjectOrganizerOnly(pid) ? router.createUrlTree(['/projects', pid]) : true))
    .catch(() => true as const);
  return budget.decide(verdict, state.url);
};

/**
 * Страницы настроек проекта больше нет: они живут в панели «Настройки» редактора мастера. Набранный
 * `/projects/:id/settings` ведёт туда; проект без пригодного мастера (легаси без указателя, свежая
 * заготовка без модели) — на «Альбомы». Организатора раньше уводит `organizerSectionRedirectGuard`.
 */
export const projectSettingsRedirectGuard: CanActivateFn = async route => {
  const pid = route.parent?.paramMap.get('projectId') ?? route.paramMap.get('projectId');
  const facade = inject(ProjectsFacade);
  const router = inject(Router);
  const budget = inject(VerdictBudget);
  if (!pid) return router.createUrlTree(['/projects']);
  const albumsTree = router.createUrlTree(['/projects', pid, 'albums']);
  const verdict = (async (): Promise<UrlTree | null> => {
    await Promise.all([facade.loadProjectBaseSettings(pid), facade.loadAlbums(pid)]);
    const masterId = facade.baseSettingsByProject()[pid]?.photoBook?.masterAlbumId;
    const albums = facade.albumsByProject()[pid] ?? [];
    // Тот же поиск, что у входа из пака (`openMasterPrintSettings`): по указателю, а без него — первый
    // безличный альбом; панель настроек рисуется на любом альбоме без владельца.
    const master = masterId ? albums.find(album => album.id === masterId) : albums.find(album => album.personId === null);
    return master && isUsableAlbum(master)
      ? // `?section=` и прочие параметры ссылки едут в панель — иначе нужная карточка не раскроется.
        router.createUrlTree(['/editor', pid, master.id], { queryParams: { ...route.queryParams, panel: 'settings' } })
      : null;
  })().catch(() => null);

  // Нет данных — «Альбомы»; не успели за бюджет — тоже «Альбомы», а мастер откроется поздно, если человек там.
  const settled = await budget.within(verdict);
  if (settled) return settled.value ?? albumsTree;
  budget.applyLate(
    verdict.then(editor => (editor ? () => budget.redirect(editor) : null)),
    router.serializeUrl(albumsTree),
  );
  return albumsTree;
};

/**
 * Любой маршрут кабинета БЕЗ `:projectId` (списки, мастера создания, тоннель дизайна, редактор дизайна,
 * аккаунт — см. `app.routes.ts`) координаторской сессии не принадлежит: без id проекта в адресе шапка
 * разворачивается в полный фотографский кабинет. Вердикт берётся из сессионного признака, а адрес — из
 * того же кэша ролей.
 */
export const organizerCabinetRedirectGuard: CanActivateFn = async (_route, state) => {
  const roles = inject(ProjectRoleService);
  const router = inject(Router);
  const budget = inject(VerdictBudget);
  const pid = roles.coordinatorProjectId();
  if (!pid) return true;
  // Метка вкладки — факт входа, но не доказательство роли: за ней могла смениться личность (вход
  // владельца в соседней вкладке перезагружает эту). Роль спрашивается у сервера, иначе владелец
  // остался бы заперт на чужом проекте, где его собственный кабинет ему уже не отдают.
  const verdict = roles
    .loadRoles(pid)
    .then(() => (roles.isProjectOrganizerOnly(pid) ? router.createUrlTree(['/projects', pid]) : true))
    .catch(() => true as const);
  return budget.decide(verdict, state.url);
};

/**
 * «Выбрать за ученика» существует ровно там, где ученик отобрал бы сам: режим `studentAssembles`,
 * отбор ещё не подтверждён (s2-ac2, s2-ac2.2). В «Автоматически» ручного отбора нет ни у кого —
 * сервер `clientSelection` в этом режиме не принимает, а мастер без выхода вернул бы к тому же
 * отказу; у фотографа CTA отбора тоже висит на `!== 'auto'`. Одно место правила — экранное меню
 * и гвард мастера читают этот же предикат.
 */
export function organizerMaySelectForStudent(
  flowMode: ProjectFlowMode | null,
  album: Pick<FC_PhotoBookDTO, 'selectionConfirmed'> | null | undefined,
): boolean {
  return flowMode === 'studentAssembles' && !album?.selectionConfirmed;
}

/** «Выбрать автоматически» шире ручного отбора: в «Автоматически» это единственный путь собрать альбом. */
export function organizerMayAutoSelect(
  flowMode: ProjectFlowMode | null,
  album: Pick<FC_PhotoBookDTO, 'selectionConfirmed'> | null | undefined,
): boolean {
  return (flowMode === 'auto' || flowMode === 'studentAssembles') && !album?.selectionConfirmed;
}

/** Мастер отбора за ученика: организатору открыт только по правилу выше, иначе — назад на «Учеников». */
export const organizerSelectWizardGuard: CanActivateFn = async route => {
  const projectId = route.paramMap.get('projectId');
  const personId = route.paramMap.get('personId');
  const roles = inject(ProjectRoleService);
  const facade = inject(ProjectsFacade);
  const router = inject(Router);
  if (!projectId || !personId) return true;

  // Права: на сбое загрузки закрываемся, а не пускаем. Роль — тоже с сервера: на свежей вкладке
  // кэш ролей пуст, и синхронное чтение пускало бы организатора в мастер по набранному адресу.
  try {
    await roles.loadRoles(projectId);
    if (!roles.isProjectOrganizerOnly(projectId)) return true;
    // Молчание сервера — тот же сбой загрузки: без предела мастер висел бы вечным спиннером.
    await withTimeout(
      Promise.all([facade.loadProjectBaseSettings(projectId), facade.loadAlbums(projectId)]),
      ACCESS_READ_LIMIT_MS,
    );
    const selection = facade.baseSettingsByProject()[projectId]?.photoBook?.clientSelection;
    // Подтверждение отбора — по ВСЕМ фотокнигам ученика, как его считает экранный срез (albumRows):
    // у ученика может быть и неудавшаяся первая сборка, и настоящий альбом, и первая строка врёт.
    const selectionConfirmed = (facade.albumsByProject()[projectId] ?? []).some(
      candidate => candidate.personId === personId && candidate.selectionConfirmed,
    );
    if (organizerMaySelectForStudent(selection ? deriveFlowMode(selection) : null, { selectionConfirmed })) return true;
  } catch {
    /* закрываемся редиректом ниже */
  }

  return router.createUrlTree(['/projects', projectId]);
};
